// @section 1 AccountAsksLens — what the client said they want
//
// Rows from hq_bd.asks, FK'd to a specific deal. Each ask carries a status
// (open / addressed-in-offer / deferred / dropped) and an optional priority
// (high / medium / low). "+ Add to offer" appends a line item to a draft
// offer pinned to this ask; the offer itself shows up in the Delivery lens.
//
// Promoted to a top-level tab on 2026-05-27 — used to be a sub-tab inside
// the retired BD lens.

import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import {
  listBdAsks, createBdAsk, patchBdAsk,
  listBdOffers, createBdOffer, createBdOfferItem,
  type AccountAggregate, type AccountDeal, type BdAsk, type BdOffer,
} from '../../api';

export default function AccountAsksLens({ data }: { data: AccountAggregate }) {
  const companyId = data.company.id;
  const deals = data.deals;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {deals.length === 0 && <NoDealsBanner />}
      <AsksSection companyId={companyId} deals={deals} />
    </div>
  );
}

// @section 2 AsksSection — list + create + per-row actions
function AsksSection({ companyId, deals }: { companyId: string; deals: AccountDeal[] }) {
  const [items, setItems] = useState<BdAsk[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const reload = () => listBdAsks(companyId).then(setItems).catch((e) => setErr(String(e)));
  useEffect(() => { reload(); }, [companyId]);

  const openCount = items?.filter((a) => a.status === 'open').length ?? 0;

  return (
    <Card title="Asks" subtitle={items ? `${openCount} open · ${items.length} total` : 'Loading…'} err={err}>
      {items !== null && (
        <>
          {items.length === 0 ? (
            <Empty>No asks logged. Capture what the client said they want — pain, scope, deliverables.</Empty>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {items.map((a) => (
                <AskRow key={a.id} ask={a} onChanged={reload} />
              ))}
            </ul>
          )}
          {deals.length > 0 && <AskForm companyId={companyId} deals={deals} onCreated={reload} />}
        </>
      )}
    </Card>
  );
}

function AskRow({ ask, onChanged }: { ask: BdAsk; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);

  async function setStatus(status: string) {
    await patchBdAsk(ask.id, { status });
    onChanged();
  }

  if (editing) {
    return (
      <li style={{ ...rowStyle, flexDirection: 'column', alignItems: 'stretch' }}>
        <AskOfferEditor
          ask={ask}
          onDone={() => { setEditing(false); onChanged(); }}
          onCancel={() => setEditing(false)}
        />
      </li>
    );
  }

  return (
    <li style={rowStyle}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13 }}>
          {ask.priority && <PriorityChip p={ask.priority} />}
          {ask.ask}
        </div>
        {ask.detail && <div style={{ fontSize: 11, color: '#6b7a90', marginTop: 2 }}>{ask.detail}</div>}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
        <StatusBadge status={ask.status} />
        {ask.status === 'open' && (
          <>
            <button onClick={() => setEditing(true)} style={{ ...btnSm, color: '#7eb6e8', borderColor: '#7eb6e844' }}>
              → Add to offer
            </button>
            <button onClick={() => setStatus('deferred')} style={btnSm}>Defer</button>
            <button onClick={() => setStatus('dropped')} style={btnSm}>Drop</button>
          </>
        )}
        {ask.status !== 'open' && (
          <button onClick={() => setStatus('open')} style={btnSm}>Reopen</button>
        )}
      </div>
    </li>
  );
}

// @section 3 AskOfferEditor — Ask → Offer line-item composer
//
// Picks an existing draft offer for this Ask's deal (or starts a new one),
// appends a line item pinned to the Ask, then flips the Ask's status to
// 'addressed-in-offer'. Three writes in sequence; the resulting offer is
// visible in the Delivery lens on next mount.
function AskOfferEditor({ ask, onDone, onCancel }: {
  ask: BdAsk; onDone: () => void; onCancel: () => void;
}) {
  const [offers, setOffers] = useState<BdOffer[] | null>(null);
  const [pickedOfferId, setPickedOfferId] = useState<string>('');
  const [name, setName] = useState(ask.ask);
  const [description, setDescription] = useState(ask.detail ?? '');
  const [qty, setQty] = useState('1');
  const [unitDollars, setUnitDollars] = useState('');
  const [recurring, setRecurring] = useState<'one-time' | 'monthly' | 'quarterly' | 'annual'>('one-time');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    listBdOffers({ dealId: ask.dealId })
      .then((list) => {
        setOffers(list);
        const firstDraft = list.find((o) => o.status === 'draft');
        setPickedOfferId(firstDraft ? firstDraft.id : '');
      })
      .catch((e) => setErr(String(e)));
  }, [ask.dealId]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      let offerId = pickedOfferId;
      if (!offerId) {
        const created = await createBdOffer({ dealId: ask.dealId });
        offerId = created.id;
      }
      const unitCents = unitDollars ? Math.round(parseFloat(unitDollars) * 100) : 0;
      await createBdOfferItem(offerId, {
        name: name.trim(),
        description: description.trim() || undefined,
        qty: parseFloat(qty) || 1,
        unitCents,
        recurring,
        addressesAskId: ask.id,
      });
      await patchBdAsk(ask.id, { status: 'addressed-in-offer' });
      onDone();
    } catch (ex) {
      setErr(String(ex));
    } finally {
      setBusy(false);
    }
  }

  if (offers === null) {
    return <div style={{ fontSize: 12, color: '#6b7a90' }}>Loading offers…</div>;
  }

  const drafts = offers.filter((o) => o.status === 'draft');

  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: 8 }}>
      <div style={{ fontSize: 11, color: '#7eb6e8', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
        Add line item for ask
      </div>
      <div style={{ fontSize: 12, color: '#a3b1c2', marginTop: -4 }}>"{ask.ask}"</div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <label style={{ fontSize: 11, color: '#6b7a90' }}>Offer:</label>
        <select value={pickedOfferId} onChange={(e) => setPickedOfferId(e.target.value)} style={{ flex: 1 }}>
          <option value="">+ New draft offer (v{(offers[0]?.version ?? 0) + 1})</option>
          {drafts.map((o) => (
            <option key={o.id} value={o.id}>
              v{o.version} {o.summary ? `· ${o.summary.slice(0, 40)}` : ''}
            </option>
          ))}
        </select>
      </div>

      <input
        placeholder="Line item name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        style={{ width: '100%' }}
      />

      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="number" step="0.5" min="0" placeholder="qty"
          value={qty} onChange={(e) => setQty(e.target.value)}
          style={{ width: 70 }}
        />
        <span style={{ color: '#6b7a90', alignSelf: 'center' }}>×</span>
        <input
          type="number" step="0.01" min="0" placeholder="unit price ($)"
          value={unitDollars} onChange={(e) => setUnitDollars(e.target.value)}
          style={{ width: 130 }}
        />
        <select value={recurring} onChange={(e) => setRecurring(e.target.value as typeof recurring)}>
          <option value="one-time">one-time</option>
          <option value="monthly">monthly</option>
          <option value="quarterly">quarterly</option>
          <option value="annual">annual</option>
        </select>
      </div>

      <input
        placeholder="description (optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        style={{ width: '100%' }}
      />

      {err && <div style={{ color: '#e87e7e', fontSize: 12 }}>{err}</div>}

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" disabled={busy || !name.trim()}>
          {busy ? 'Saving…' : 'Add to offer + mark addressed'}
        </button>
        <button type="button" onClick={onCancel} style={btnSm}>Cancel</button>
      </div>
    </form>
  );
}

function AskForm({ companyId, deals, onCreated }: { companyId: string; deals: AccountDeal[]; onCreated: () => void }) {
  const [dealId, setDealId] = useState(deals[0].id);
  const [text, setText] = useState('');
  const [priority, setPriority] = useState<'high' | 'medium' | 'low' | ''>('');
  const [busy, setBusy] = useState(false);

  return (
    <form
      style={formStyle}
      onSubmit={async (e) => {
        e.preventDefault();
        if (!text.trim()) return;
        setBusy(true);
        try {
          await createBdAsk({ companyId, dealId, ask: text.trim(), priority: priority || undefined });
          setText(''); setPriority('');
          onCreated();
        } finally { setBusy(false); }
      }}
    >
      <DealPicker deals={deals} value={dealId} onChange={setDealId} />
      <input placeholder="What did they ask for?" value={text} onChange={(e) => setText(e.target.value)} style={{ flex: 1 }} />
      <select value={priority} onChange={(e) => setPriority(e.target.value as 'high' | 'medium' | 'low' | '')}>
        <option value="">Priority…</option>
        <option value="high">High</option>
        <option value="medium">Medium</option>
        <option value="low">Low</option>
      </select>
      <button type="submit" disabled={busy || !text.trim()}>Add</button>
    </form>
  );
}

// @section 4 Shared local helpers
function NoDealsBanner() {
  return (
    <div style={{ ...cardStyle, borderStyle: 'dashed' }}>
      <div style={{ fontSize: 13, color: '#a3b1c2' }}>
        This account has no deals yet. Asks attach to a specific deal — create one in the Overview lens first.
      </div>
    </div>
  );
}

function Card({ title, subtitle, err, children }: { title: string; subtitle?: string; err?: string | null; children: React.ReactNode }) {
  return (
    <section style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 14, textTransform: 'uppercase', letterSpacing: 1.5, color: '#a3b1c2' }}>{title}</h3>
        {subtitle && <span style={{ fontSize: 11, color: '#6b7a90' }}>{subtitle}</span>}
      </div>
      {err && <div style={{ color: '#e87e7e', fontSize: 12, marginBottom: 8 }}>{err}</div>}
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <em style={{ color: '#6b7a90', fontSize: 13 }}>{children}</em>;
}

function DealPicker({ deals, value, onChange }: { deals: AccountDeal[]; value: string; onChange: (v: string) => void }) {
  if (deals.length === 1) {
    return <span style={{ fontSize: 11, color: '#6b7a90' }}>deal: {deals[0].name}</span>;
  }
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {deals.map((d) => (
        <option key={d.id} value={d.id}>{d.name}{d.closedAt ? ' (closed)' : ''}</option>
      ))}
    </select>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tone = status === 'open' ? '#7eb6e8'
    : status === 'addressed-in-offer' ? '#6ec78a'
    : status === 'deferred' ? '#a08fd8'
    : status === 'dropped' ? '#6b7a90'
    : '#a3b1c2';
  return (
    <span style={{
      fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600,
      padding: '2px 8px', borderRadius: 4,
      background: `${tone}22`, color: tone,
    }}>{status}</span>
  );
}

function PriorityChip({ p }: { p: string }) {
  const tone = p === 'high' ? '#e87e7e' : p === 'medium' ? '#f0c674' : '#7eb6e8';
  return (
    <span style={{
      fontSize: 9, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700,
      padding: '1px 6px', borderRadius: 3, marginRight: 8,
      background: `${tone}22`, color: tone,
    }}>{p}</span>
  );
}

const cardStyle: CSSProperties = {
  border: '1px solid #232a37',
  borderRadius: 8,
  padding: 14,
  background: '#171c26',
};
const rowStyle: CSSProperties = {
  padding: '8px 0',
  borderTop: '1px solid #232a37',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
};
const formStyle: CSSProperties = {
  marginTop: 12,
  paddingTop: 12,
  borderTop: '1px dashed #232a37',
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  flexWrap: 'wrap',
};
const btnSm: CSSProperties = {
  fontSize: 11,
  padding: '3px 8px',
  border: '1px solid #2a3346',
  borderRadius: 4,
  background: 'transparent',
  color: '#a3b1c2',
  cursor: 'pointer',
};
